import type { Int32StringKeyValuePair, VideoResponsePagedData } from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { Pen } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { profileAdmin } from '@on3/ui-lib/utils/auth/constants';
import clsx from 'clsx';
import { VideoPlayer } from 'components/Video/VideoPlayer';
import useAthlete from 'contexts/AthleteProvider';
import { useMemo, useState } from 'react';

import { VideosMock } from './Mock';
import styles from './PlayerVideosModule.module.scss';

interface IPlayerVideosModuleProps {
  videoData?: VideoResponsePagedData | null;
}

export const PlayerVideosModule = ({ videoData }: IPlayerVideosModuleProps) => {
  const { athlete, hasBaseProfile } = useAthlete();
  const { user } = useAuth();
  const { list } = videoData || {};
  const [categoryId, setCategoryId] = useState(-1);
  const [videoList, setVideoList] = useState(list);

  const canEdit = userHasPermission(profileAdmin, user) && hasBaseProfile;

  const categoryList = useMemo(() => {
    const catList: Int32StringKeyValuePair[] = [{ key: -1, value: 'All' }];

    return (list ?? []).reduce((acc, curr) => {
      if (!acc.find((x) => x.key === curr.category?.key)) {
        if (curr.category) acc.push(curr.category);
      }

      return acc;
    }, catList);
  }, [list]);

  const handleCategory = (key: number) => {
    setCategoryId(key);

    key < 0
      ? setVideoList(list)
      : setVideoList(list?.filter((x) => x.category?.key === key));
  };

  if (!videoData?.list?.length) {
    return <VideosMock />;
  }

  return (
    <section className={styles.block}>
      <header className={styles.header}>
        <h2>Videos</h2>
        <div className={styles.filters}>
          {categoryList?.map((category) => (
            <button
              className={clsx(styles.btn, {
                [styles.active]: categoryId === category.key,
              })}
              key={category.key}
              onClick={() => handleCategory(category.key)}
              type="button"
            >
              {category.value}
            </button>
          ))}
        </div>
      </header>
      <ul className={styles.videos}>
        {videoList?.map((video) => (
          <li key={video.key}>
            <div className={styles.video}>
              <VideoPlayer
                sourceUrl={video.sourceUrl ?? ''}
                thumbnail={video.thumbnail ?? ''}
                title={video.title ?? ''}
              />
            </div>
            {video.title && <h3>{video.title}</h3>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div>
          <Link
            className={styles.edit}
            href={`/db/${athlete.slug}/elite/network/profile/videos/`}
          >
            <Pen />
            Edit Videos
          </Link>
        </div>
      )}
    </section>
  );
};
